import React, { type ReactElement, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Checkbox, CircularProgress, FormControlLabel } from '@mui/material'
import debounce from 'lodash/debounce'

import { apiZodios } from '~api/apiZodios'
import {
  Avatar,
  Button,
  Icon,
  IconButton,
  Search,
  TextField,
  Typography,
  formatPhoneNumber,
} from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { typography } from '~tokens/internal'
import { type ContactEntry2, type RelatedContactEntry2, formatAddress0 } from '~types'

import './AddRelatedContactForm.scss'

interface AddRelatedContactProps {
  contactEntryData?: ContactEntry2
  editContact?: RelatedContactEntry2 | null
  reverseRelatedContacts?: RelatedContactEntry2[]
  handleAddRelatedContact: (
    contact: ContactEntry2,
    relationship: string,
    createReverse: boolean,
    reverseName: string,
    deleteReverse: boolean,
  ) => void
  onDelete?: (contactId: string) => void
}

const AddRelatedContact = ({
  contactEntryData,
  editContact,
  reverseRelatedContacts,
  handleAddRelatedContact,
  onDelete,
}: AddRelatedContactProps): ReactElement => {
  const user = useSelector((state: RootState) => state?.auth.user)
  const organization = useSelector((state: RootState) => state?.organization.selectedOrganization)
  const [selectedContact, setSelectedContact] = useState<ContactEntry2 | null>(
    editContact ? editContact : null,
  )
  const [relationship, setRelationship] = useState<string>(editContact ? editContact.relation : '')

  const existingReverse = editContact
    ? reverseRelatedContacts?.find((rc) => rc.id === editContact.id)
    : undefined

  const [hasExistingReverse, setHasExistingReverse] = useState<boolean>(!!existingReverse)
  const [createReverse, setCreateReverse] = useState<boolean>(!!existingReverse)
  const [reverseName, setReverseName] = useState<string>(existingReverse?.relation ?? '')

  const isEditMode = !!editContact

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [searchResults, setSearchResults] = useState<ContactEntry2[]>([])

  const searchContacts = async (query: string): Promise<void> => {
    const bookIdentityId = contactEntryData
      ? contactEntryData.owner_identity_id
      : organization?.contacts_owner_identity_id
    if (!user || !bookIdentityId || query.length < 2) {
      setSearchResults([])
      return
    }

    try {
      setIsSearching(true)
      const response = await apiZodios.contacts_searchContactEntries({
        user_id: user?.id,
        organization_identity_id: bookIdentityId,
        contact_filter: {
          book: bookIdentityId,
        },
        query: query,
        limit: 10,
      })
      setSearchResults(
        contactEntryData
          ? response.contact_entries.filter((contact) => contact.id !== contactEntryData.id)
          : response.contact_entries,
      )
    } catch (error) {
      console.error('Error searching contacts:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      searchContacts(query)
    }, 200),
    [contactEntryData, organization, user],
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedSearch(value)
  }

  const handleSearchClear = (): void => {
    setSearchTerm('')
    setSearchResults([])
    debouncedSearch.cancel()
  }

  const contactDisplay = (contact: ContactEntry2, onRemove?: () => void): ReactElement => {
    return (
      <Box
        key={contact.id}
        className='add-related-contact__contact-card'
        onClick={() => setSelectedContact(contact)}
      >
        {onRemove && (
          <Box className='add-related-contact__remove'>
            <IconButton
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
              size='small'
            >
              <Icon name='remove' size={typography.fontSize.md} />
            </IconButton>
          </Box>
        )}
        <Box className='add-related-contact__contact-header'>
          <Avatar src={contact.image || undefined} alt={contact.name} size='sm' />
          <Typography variant='body1'>{contact.name}</Typography>
        </Box>

        {contact.emails.length > 0 && (
          <Box className='add-related-contact__contact-detail'>
            <Typography className='add-related-contact__detail-text'>
              {contact.emails[0].address}
            </Typography>
            {contact.emails.length > 1 && (
              <Typography className='add-related-contact__detail-more'>
                +{contact.emails.length - 1} more
              </Typography>
            )}
          </Box>
        )}

        {contact.phones.length > 0 && (
          <Box className='add-related-contact__contact-detail'>
            <Typography className='add-related-contact__detail-text'>
              {formatPhoneNumber(contact.phones[0].phone_number)}
            </Typography>
            {contact.phones.length > 1 && (
              <Typography className='add-related-contact__detail-more'>
                +{contact.phones.length - 1} more
              </Typography>
            )}
          </Box>
        )}

        {contact.addresses.length > 0 && (
          <Box className='add-related-contact__contact-detail'>
            <Typography className='add-related-contact__detail-text'>
              {formatAddress0(contact.addresses[0])}
            </Typography>
            {contact.addresses.length > 1 && (
              <Typography className='add-related-contact__detail-more'>
                +{contact.addresses.length - 1} more
              </Typography>
            )}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box className='add-related-contact'>
      {selectedContact ? (
        <Box className='add-related-contact__selected'>
          <Box>
            {contactDisplay(
              selectedContact,
              isEditMode ? undefined : (): void => setSelectedContact(null),
            )}
          </Box>
          <Box className='add-related-contact__relationship'>
            <TextField
              label='Relationship'
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              fullWidth
            />
          </Box>

          <>
            {hasExistingReverse ? (
              <Box className='add-related-contact__reverse-name'>
                <TextField
                  label='Reverse Relationship Name'
                  value={reverseName}
                  onChange={(e) => setReverseName(e.target.value)}
                  fullWidth
                />
                <IconButton
                  onClick={() => {
                    setHasExistingReverse(false)
                    setCreateReverse(false)
                    setReverseName('')
                  }}
                  size='small'
                  className='add-related-contact__remove-button'
                >
                  <Icon name='clear' size={typography.fontSize.md} />
                </IconButton>
              </Box>
            ) : (
              <>
                <Box className='add-related-contact__checkbox'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={createReverse}
                        onChange={(e, checked) => {
                          setCreateReverse(checked)
                          if (!checked) {
                            setReverseName('')
                          }
                        }}
                      />
                    }
                    label='Create Reverse Relationship'
                  />
                </Box>

                {createReverse && (
                  <Box className='add-related-contact__reverse-name'>
                    <TextField
                      label='Reverse Relationship Name'
                      value={reverseName}
                      onChange={(e) => setReverseName(e.target.value)}
                      fullWidth
                    />
                  </Box>
                )}
              </>
            )}
          </>

          <Box className='add-related-contact__actions'>
            {isEditMode && onDelete && (
              <Button
                color='error'
                onClick={() => {
                  onDelete(selectedContact.id)
                }}
              >
                Delete Relation
              </Button>
            )}
            <Box className='add-related-contact__standard-actions'>
              <Button
                variant='outlined'
                onClick={() => {
                  setSelectedContact(null)
                  setSearchTerm('')
                  setSearchResults([])
                }}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                onClick={(): void => {
                  const shouldDeleteReverse = !!existingReverse && !hasExistingReverse
                  handleAddRelatedContact(
                    selectedContact,
                    relationship,
                    createReverse,
                    reverseName,
                    shouldDeleteReverse,
                  )
                  setRelationship('')
                  setCreateReverse(false)
                  setReverseName('')
                  setSearchTerm('')
                  setSelectedContact(null)
                  setSearchResults([])
                }}
                size='medium'
                disabled={relationship === '' || (createReverse && reverseName === '')}
              >
                {isEditMode ? 'Save' : 'Add Relation'}
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box className='add-related-contact__search-section'>
          <Search
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            placeholder='Search for a user'
          />

          {(isSearching || searchResults.length > 0 || searchTerm.length > 0) && (
            <Box className='add-related-contact__results'>
              {isSearching ? (
                <Box className='add-related-contact__loading'>
                  <CircularProgress size={typography.fontSize.lg} />
                </Box>
              ) : searchResults.length > 0 ? (
                searchResults.map((contact) => contactDisplay(contact))
              ) : searchTerm.length > 0 ? (
                <Typography className='add-related-contact__empty' color='textSecondary'>
                  No contacts found
                </Typography>
              ) : null}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default AddRelatedContact
